import { IconFunctionalInformationCircle16 } from '@cian/ui-kit/icons';
import { Suggest, type ISuggestionDefaultItem } from '@cian/ui-kit/suggest';
import { type FC, type KeyboardEvent, useCallback } from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetError,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { needsManualPostalCode, validatePostalCode, validateSelectedAddress } from './addressRules';
import style from './FormStep.css';
import { IFormData } from './FormStep.types';
import { useAddressSuggest } from '../../../hooks';
import type { IDadataSuggestion } from '../../../services/dadataSuggest';

interface IAddressFieldProps {
  clearErrors: UseFormClearErrors<IFormData>;
  control: Control<IFormData>;
  errors: FieldErrors<IFormData>;
  getValues: UseFormGetValues<IFormData>;
  register: UseFormRegister<IFormData>;
  setError: UseFormSetError<IFormData>;
  setValue: UseFormSetValue<IFormData>;
}

export const AddressField: FC<IAddressFieldProps> = ({
  clearErrors,
  control,
  errors,
  getValues,
  register,
  setError,
  setValue,
}) => {
  const addressSuggest = useAddressSuggest();
  const selectedAddress = useWatch({ control, name: 'selectedAddress' }) ?? null;

  const validateAddressOnBlur = useCallback(
    (onBlur: () => void) => {
      if (!addressSuggest.open) {
        onBlur();
      }
    },
    [addressSuggest.open],
  );

  const mapSuggestions = useCallback(
    (items: IDadataSuggestion[]): ISuggestionDefaultItem<IDadataSuggestion>[] =>
      items.map(item => ({
        title: item.value,
        value: item,
      })),
    [],
  );

  const validateAddress = useCallback(
    (value: string) => validateSelectedAddress(getValues('selectedAddress'), value),
    [getValues],
  );

  const applySelectedAddressValidation = useCallback(
    (suggestion: IDadataSuggestion) => {
      const validationResult = validateSelectedAddress(suggestion, suggestion.unrestricted_value);

      if (validationResult === true) {
        clearErrors(['address', 'postalCode']);

        return;
      }

      clearErrors('postalCode');
      setError('address', { type: 'validate', message: validationResult });
    },
    [clearErrors, setError],
  );

  const validateManualPostalCode = useCallback(
    (value: string) => validatePostalCode(getValues('selectedAddress'), value),
    [getValues],
  );

  const handleAddressKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, onBlur: () => void) => {
      if (event.key === 'Enter' && addressSuggest.open) {
        event.preventDefault();
      }

      if (event.key === 'Tab' && addressSuggest.open) {
        addressSuggest.onClose();
        onBlur();
      }
    },
    [addressSuggest],
  );

  const isPostalCodeFieldVisible = needsManualPostalCode(selectedAddress);

  return (
    <div className={style['field']}>
      <p className={style['label']}>
        <span>Адрес </span>
        <span className={style['label-hint']}>(для доставки Почтой России)</span>
      </p>
      <Controller
        name="address"
        control={control}
        rules={{
          required: 'Укажите адрес доставки',
          validate: validateAddress,
        }}
        render={({ field }) => (
          <div
            className={`${style['address-row']} ${
              isPostalCodeFieldVisible ? style['address-row-with-postal-code'] : ''
            }`.trim()}
          >
            <div className={style['address-field']}>
              <Suggest<IDadataSuggestion>
                open={addressSuggest.open}
                suggestions={mapSuggestions(addressSuggest.suggestions)}
                highlightValue={addressSuggest.inputValue}
                onSelect={item => {
                  addressSuggest.onSelect(item.value);
                  setValue('selectedAddress', item.value);
                  setValue('address', item.value.unrestricted_value);
                  setValue('postalCode', '');
                  applySelectedAddressValidation(item.value);
                }}
                onClose={event => {
                  addressSuggest.onClose();

                  if (event.source === 'outsideClick' || event.source === 'mobileModal') {
                    field.onBlur();
                  }
                }}
              >
                <input
                  aria-label="Адрес доставки"
                  className={`${style['input']} ${errors.address ? style['input-error'] : ''}`.trim()}
                  type="text"
                  value={addressSuggest.inputValue}
                  onChange={e => {
                    const nextAddress = e.target.value;

                    addressSuggest.onChange(nextAddress);
                    setValue('selectedAddress', null);
                    setValue('address', nextAddress);
                    setValue('postalCode', '');
                    clearErrors(['address', 'postalCode']);
                  }}
                  onBlur={() => {
                    validateAddressOnBlur(field.onBlur);
                  }}
                  onKeyDown={e => {
                    handleAddressKeyDown(e, field.onBlur);
                  }}
                />
              </Suggest>
            </div>
            {isPostalCodeFieldVisible && (
              <div className={style['postal-code-field']}>
                <input
                  aria-label="Индекс"
                  className={`${style['input']} ${errors.postalCode ? style['input-error'] : ''}`.trim()}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Индекс"
                  type="text"
                  {...register('postalCode', {
                    validate: validateManualPostalCode,
                  })}
                />
              </div>
            )}
          </div>
        )}
      />
      {errors.address && <span className={style['error']}>{errors.address.message}</span>}
      {errors.postalCode && <span className={style['error']}>{errors.postalCode.message}</span>}
      <div className={style['address-disclaimer']}>
        <span className={style['address-disclaimer-icon']} aria-hidden="true">
          <IconFunctionalInformationCircle16 color="current_color" />
        </span>
        <p className={style['address-disclaimer-text']}>
          Проверьте адрес доставки: сможем доставить наклейки только по полным и точным адресам
        </p>
      </div>
    </div>
  );
};
